import React, { useState, useEffect } from "react";
import { Button, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ClassroomResult, CoreClassroomAdminClassroom, CoreClassroomAdminClassroomListing, CoreClassroomAdminCourseState } from "../../../../models/CoreClassroomAdminClassroom";
import { ClassroomChangeStatus, ClearClassroomChangeStatus } from "../../../../store/ClassroomAdmin/ClassroomToolsAction";
import StatusCode from "../../../../util/StatusCode";
import Loading from "../../../misc/Loading";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SelectInput from "../../../misc/SelectInput";
import * as Helper from '../../../../util/Helper';


export const StatusOptions = [
    CoreClassroomAdminCourseState.Unknown,
    CoreClassroomAdminCourseState.Active,
    CoreClassroomAdminCourseState.Archived,
];

interface ClassroomViewToolChangeStatusProps {
    classrooms: CoreClassroomAdminClassroom[] | CoreClassroomAdminClassroomListing[]
}

const ClassroomViewToolChangeStatus = (props: ClassroomViewToolChangeStatusProps) => {
    const { t } = useTranslation(['common', "classroomAdmin"]);
    const dispatch = useDispatch();
    const [status, setStatus] = useState<CoreClassroomAdminCourseState>(CoreClassroomAdminCourseState.Unknown);
    const [confirmed, setConfirmed] = useState<boolean>(false);

    const [changeStatusClassroomState, setChangeStatusClassroomState] = useState<ClassroomResult[]>([]);

    useEffect(() => {
        const state = props.classrooms.map(x => ({
            classroomId: x.classroomId,
            code: StatusCode.NONE,
        }));
        setChangeStatusClassroomState(state);

        return () => {
            ClearClassroomChangeStatus()(dispatch);
            setConfirmed(false);
        };
    }, []);

    const updateChangeStatus = async () => {
        setConfirmed(true);

        //Synched and archived won´t be sent
        const classroomsToUpdate =
            status === CoreClassroomAdminCourseState.Archived
                ? props.classrooms.filter(x => !x.synched)
                : props.classrooms;

        const pendingState = props.classrooms.map(x => ({
            classroomId: x.classroomId,
            code: classroomsToUpdate.some(c => c.classroomId === x.classroomId)
                ? StatusCode.PENDING
                : StatusCode.NONE,
        }));

        setChangeStatusClassroomState(pendingState);

        const results = await Promise.all(
            classroomsToUpdate.map(x => ClassroomChangeStatus(x.classroomId, status)(dispatch))
        );

        const resultById = new Map(results.map(x => [x.classroomId, x]));
        const mergedState = pendingState.map(x => {
            const updated = resultById.get(x.classroomId);
            return updated ? updated : x;
        });

        setChangeStatusClassroomState(mergedState);
    }

    const renderStatus = (classroomId: string) => {
        if (!confirmed) {
            return;
        }

        var classroom = changeStatusClassroomState.find(x => x.classroomId === classroomId);
        if (!classroom) {
            return;
        }

        if (classroom.code === StatusCode.NONE) {
            return t(`tools.CHANGE_STATUS.cannot`, { ns: 'classroomAdmin' })
        }

        if (classroom.code === StatusCode.PENDING) {
            return <Loading />;
        }
        if (classroom.code === StatusCode.COMPLETE) {
            return (<>
                <FontAwesomeIcon icon="check" color="green" style={{ marginRight: '5px' }} />
                {t(`tools.CHANGE_STATUS.success`, { ns: 'classroomAdmin' })}
            </>);
        }

        if (classroom.code === StatusCode.ERROR) {
            const errorData = classroom?.error && classroom.error.response?.data as { title?: string; detail?: string } | undefined;
            const title = errorData?.title;
            const errorMessage = title ? t(`tools.errors.general.${title}`, { ns: 'classroomAdmin' }) : t(`error.somethingWentWrong`, { ns: 'common' });
            return (<>
                <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
                {errorMessage}
            </>);
        }
    }

    return (<>
        <Table striped>
            <thead>
                <tr>
                    <th>{t('tools.tableHeader.classroom', { ns: 'classroomAdmin' })}</th>
                    <th>{t('tools.CHANGE_STATUS.synched', { ns: 'classroomAdmin' })}</th>
                    <th>{t('tools.CHANGE_STATUS.currentStatus', { ns: 'classroomAdmin' })}</th>
                    <th>{t('tools.tableHeader.status', { ns: 'classroomAdmin' })}</th>
                </tr>
            </thead>
            <tbody>
                {props.classrooms.map(x => (
                    <tr key={x.classroomId}>
                        <td>{x.classroomName}</td>
                        <td>{Helper.active(x.synched)}</td>
                        <td> {t(`classroomAdmin.state.${x.state.courseState}`, { ns: 'classroomAdmin' })}</td>
                        <td>{renderStatus(x.classroomId)}</td>
                    </tr>
                ))}
            </tbody>
        </Table>

        <div className="mb-3 mt-3">
            <span><b>{t('tools.CHANGE_STATUS.newStatus', { ns: 'classroomAdmin' })}:</b></span>
            <SelectInput
                type='number'
                items={StatusOptions
                    .filter(x => x !== CoreClassroomAdminCourseState.Unknown)
                    .map(key => ({
                        value: key,
                        text: t(`classroomAdmin.state.${CoreClassroomAdminCourseState[key]}`, { ns: 'classroomAdmin' })
                    }))}
                onChange={(_, value) => {
                    if (value !== undefined) {
                        setStatus(value as CoreClassroomAdminCourseState);
                    }
                }}
                name='status'
                selected={status}
                defaultText={t(`tools.CHANGE_STATUS.placeholderSelect`, { ns: 'classroomAdmin' })}
            />
        </div>

        {props.classrooms.some(x => x.synched) && status === CoreClassroomAdminCourseState.Active
            ? <div className="mb-3" style={{ padding: '20px 40px', border: '1px solid red', margin: '0 20px', backgroundColor: 'pink' }}>
                {t(`tools.CHANGE_STATUS.warning`, { ns: 'classroomAdmin' })}</div>
            : null}

        <div className="flex align-items-center">
            <Button onClick={() => updateChangeStatus()} disabled={!status} style={{ marginRight: '15px' }}>{t('action.confirm', { ns: 'common' })}</Button>
        </div>
    </>);
}

export default ClassroomViewToolChangeStatus;
